import { useWatch } from 'react-hook-form';
import { Grid, GridColumn, Message, Segment, SegmentGroup } from 'semantic-ui-react';
import { Label, TitleSegment } from '@britishcouncil/react-admin';
import { Timestamp } from '@britishcouncil/common-core';

import {
  RHFormDatePicker,
  RHFormSelect,
  RHFormCheckbox,
  RHFormTextarea,
  useFormContext,
} from 'components';
import { formatDate } from 'utils';
import { OnlineSpeakingMonitoringResult } from 'api';
import { OnlineSpeakingMonitoringFormData } from './onlineSpeakingMonitoringValidation';
import { ONLINE_SPEAKING_MONITORING_TYPE_OPTIONS } from './onlineSpeakingMonitoringOptions';
import { useOnlineSpeakingMonitoringResultOptions } from './useOnlineSpeakingMonitoringResultOptions';

export const OnlineSpeakingMonitoringForm = ({
  deleted,
  archived,
  loading,
  monitoringId,
}: {
  deleted?: { deletedOn?: string | null; deletedBy?: string };
  archived?: { archivedOn?: string | null; archivedBy?: string };
  loading?: boolean;
  monitoringId?: number;
}) => {
  const {
    formMethods: { control },
  } = useFormContext<OnlineSpeakingMonitoringFormData>();
  const result = useWatch({ control, name: 'result' });
  const { resultOptions, isLoadingResults } =
    useOnlineSpeakingMonitoringResultOptions(monitoringId);
  const isFollowUpHidden = result === OnlineSpeakingMonitoringResult.Phase0;

  return (
    <SegmentGroup>
      <TitleSegment
        title={
          <>
            Monitoring Details {deleted?.deletedBy && <Label color="red">Deleted</Label>}
            {archived?.archivedBy && <Label color="yellow">Archived</Label>}
          </>
        }
      />
      <Segment loading={loading}>
        <Grid columns={4} stackable>
          <GridColumn>
            <RHFormDatePicker
              label="Monitoring date"
              name="monitoringDate"
              testId="monitoring-date"
              control={control}
              required
              toDate={Timestamp.now()}
              placeholder="Select date"
            />
          </GridColumn>
          <GridColumn>
            <RHFormSelect
              label="Monitoring type"
              name="type"
              data-testid="monitoring-type"
              control={control}
              required
              options={ONLINE_SPEAKING_MONITORING_TYPE_OPTIONS}
              placeholder="Select a type ..."
            />
          </GridColumn>
          <GridColumn>
            <RHFormSelect
              label="Monitoring result"
              name="result"
              data-testid="monitoring-result"
              control={control}
              required
              options={resultOptions}
              loading={isLoadingResults}
              placeholder="Select a result ..."
            />
          </GridColumn>
          {!isFollowUpHidden && (
            <GridColumn>
              <RHFormCheckbox
                label="Follow up completed"
                name="hasFollowUp"
                data-testid="monitoring-follow-up"
                control={control}
              />
            </GridColumn>
          )}
          <GridColumn width={16}>
            <RHFormTextarea
              label="Comment"
              name="comment"
              data-testid="monitoring-comment"
              control={control}
              rows={3}
            />
          </GridColumn>
        </Grid>

        {deleted?.deletedBy && (
          <Message warning>
            <Message.Content>
              This Online Speaking monitoring was deleted by {deleted.deletedBy} on{' '}
              {formatDate(deleted.deletedOn)}.
            </Message.Content>
          </Message>
        )}
        {archived?.archivedBy && (
          <Message warning>
            <Message.Content>
              This Online Speaking monitoring was archived by {archived.archivedBy} on{' '}
              {formatDate(archived.archivedOn)}.
            </Message.Content>
          </Message>
        )}
      </Segment>
    </SegmentGroup>
  );
};
